import { ClockCircleOutlined, ProjectOutlined } from '@ant-design/icons';
import { ArchiveBox } from '../../../assets/CustomIcons/ArchiveBox';
import { BacklogCircle } from '../../../assets/CustomIcons/BacklogCircle';
import { BarChartIcon } from '../../../assets/CustomIcons/BarChartIcon';
import { EpicIcon } from '../../../assets/CustomIcons/EpicIcon';
import { SprintIcon } from '../../../assets/CustomIcons/SprintIcon';
import { TriageTriangle } from '../../../assets/CustomIcons/TriageTriangle';
import { WorkspaceFramework } from '../../../types/Workspace';

export enum SideBarItemEnum {
  triage = 'triage',
  epics = 'epics',
  backlog = 'backlog',
  planning = 'planning',
  sprint = 'sprint',
  kanban = 'kanban',
  analytics = 'analytics',
  archive = 'archive',
  retrospectives = 'retrospectives',
  reports = 'reports',
}

export type SideBarItem = {
  key: SideBarItemEnum;
  label: string;
  icon: JSX.Element;
  /** Short tag shown after the label, e.g. "MCP" on Analytics. Hidden when the sidebar is collapsed. */
  badge?: string;
};

const isTriageArchiveEnabled = import.meta.env.VITE_FEATURE_TRIAGE_ARCHIVE === 'true';

export const getSideBarItemGroups = (framework: WorkspaceFramework): SideBarItem[] => {
  const epicsItem: SideBarItem = {
    label: SideBarUtils.getTitle(SideBarItemEnum.epics),
    key: SideBarItemEnum.epics,
    icon: <EpicIcon />,
  };
  const triageItem: SideBarItem = {
    label: SideBarUtils.getTitle(SideBarItemEnum.triage),
    key: SideBarItemEnum.triage,
    icon: <TriageTriangle />,
  };
  const archiveItem: SideBarItem = {
    label: SideBarUtils.getTitle(SideBarItemEnum.archive),
    key: SideBarItemEnum.archive,
    icon: <ArchiveBox />,
  };
  const backlogItem: SideBarItem = {
    label: SideBarUtils.getTitle(SideBarItemEnum.backlog),
    key: SideBarItemEnum.backlog,
    icon: <BacklogCircle />,
  };
  const analyticsItem: SideBarItem = {
    label: SideBarUtils.getTitle(SideBarItemEnum.analytics),
    key: SideBarItemEnum.analytics,
    icon: <BarChartIcon />,
    // Analytics runs through MCP; the tag makes MCP discoverable from the sidebar. (#3076)
    badge: 'MCP',
  };

  let middleGroup: SideBarItem[];
  if (framework === WorkspaceFramework.scrum) {
    middleGroup = [
      backlogItem,
      {
        label: SideBarUtils.getTitle(SideBarItemEnum.planning),
        key: SideBarItemEnum.planning,
        icon: <ClockCircleOutlined />,
      },
      {
        label: SideBarUtils.getTitle(SideBarItemEnum.sprint),
        key: SideBarItemEnum.sprint,
        icon: <SprintIcon />,
      },
    ];
  } else if (framework === WorkspaceFramework.kanban) {
    middleGroup = [
      backlogItem,
      {
        label: SideBarUtils.getTitle(SideBarItemEnum.kanban),
        key: SideBarItemEnum.kanban,
        icon: <ProjectOutlined />,
      },
    ];
  } else {
    middleGroup = [backlogItem];
  }

  // Triage leads and Archive trails the middle group when the feature is enabled.
  if (isTriageArchiveEnabled) {
    middleGroup = [triageItem, ...middleGroup, archiveItem];
  }

  // Single flat list, in display order: Epics, then the middle group, then Analytics last.
  return [epicsItem, ...middleGroup, analyticsItem];
};

export const SideBarUtils = {
  getTitle(item: SideBarItemEnum): string {
    switch (item) {
      case SideBarItemEnum.triage:
        return 'Triage';
      case SideBarItemEnum.backlog:
        return 'Backlog';
      case SideBarItemEnum.epics:
        return 'Epics';
      case SideBarItemEnum.planning:
        return 'Planning';
      case SideBarItemEnum.sprint:
        return 'Sprint';
      case SideBarItemEnum.kanban:
        return 'Kanban';
      case SideBarItemEnum.analytics:
        return 'Analytics';
      case SideBarItemEnum.archive:
        return 'Archive';
      case SideBarItemEnum.retrospectives:
        return 'Retrospectives';
      case SideBarItemEnum.reports:
        return 'Reports';
    }
  },
};
